import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, CheckCircle2, Plus, Send, Sparkles, Upload, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { Avatar } from '@/components/ui/avatar';
import { Modal } from '@/components/ui/drawer';
import { Input, Select } from '@/components/ui/input';
import { api, cn, formatCurrency } from '@/lib/utils';
import { CsvImportModal } from '@/legacy/components/csv-import-modal';
import { useImportModalFromSearch } from '@/lib/import-search';
import { EQUIPMENT_TYPES } from '@maintenance-tracker/shared';

interface EquipmentItem {
  id: string;
  type: string;
  name: string;
  installYear: number;
  expectedLifeYears: number;
  lifePercent: number;
  estimatedJobValue: number;
  quoteSentAt: string | null;
  notes: string | null;
}

interface UpgradeItem {
  contactId: string;
  contactName: string;
  contactEmail: string | null;
  agreementTitle: string | null;
  equipment: EquipmentItem[];
  totalEstimatedValue: number;
  quoteSent: boolean;
}

export function UpgradesPage() {
  const qc = useQueryClient();
  const [quoteOpen, setQuoteOpen] = useState<UpgradeItem | null>(null);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [convertOpen, setConvertOpen] = useState<UpgradeItem | null>(null);
  const [convertAmount, setConvertAmount] = useState(0);
  const [equipmentImportOpen, setEquipmentImportOpen] = useState(false);
  useImportModalFromSearch(setEquipmentImportOpen, ['equipment', 'generic']);
  const [addEquipmentOpen, setAddEquipmentOpen] = useState(false);
  const [equipmentContactId, setEquipmentContactId] = useState('');
  const [equipmentForm, setEquipmentForm] = useState({
    type: 'furnace' as (typeof EQUIPMENT_TYPES)[number],
    name: '',
    install_year: new Date().getFullYear() - 12,
    notes: '',
  });

  const { data: contactsData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api<{ contacts: Array<{ id: string; name: string }> }>('/api/v1/contacts'),
    enabled: addEquipmentOpen,
  });

  const addEquipment = useMutation({
    mutationFn: () =>
      api(`/api/v1/contacts/${equipmentContactId}/equipment`, {
        method: 'POST',
        body: JSON.stringify(equipmentForm),
      }),
    onSuccess: () => {
      setAddEquipmentOpen(false);
      setEquipmentContactId('');
      setEquipmentForm({
        type: 'furnace',
        name: '',
        install_year: new Date().getFullYear() - 12,
        notes: '',
      });
      qc.invalidateQueries({ queryKey: ['upgrades'] });
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['upgrades'],
    queryFn: () => api<{ items: UpgradeItem[] }>('/api/v1/opportunities/upgrades'),
  });

  const quote = useMutation({
    mutationFn: ({ contactId, notes }: { contactId: string; notes?: string }) =>
      api(`/api/v1/opportunities/upgrades/${contactId}/quote`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),
    onSuccess: () => {
      setQuoteOpen(null);
      setQuoteNotes('');
      qc.invalidateQueries({ queryKey: ['upgrades'] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const convert = useMutation({
    mutationFn: ({ contactId, amount }: { contactId: string; amount: number }) =>
      api(`/api/v1/opportunities/upgrades/${contactId}/convert`, {
        method: 'POST',
        body: JSON.stringify({ amount }),
      }),
    onSuccess: () => {
      setConvertOpen(null);
      qc.invalidateQueries({ queryKey: ['upgrades'] });
      qc.invalidateQueries({ queryKey: ['opportunities-funnel'] });
    },
  });

  const items = data?.items ?? [];
  const totalValue = items.reduce((sum, i) => sum + i.totalEstimatedValue, 0);

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-sm text-text-secondary">Could not load equipment leads. Try again in a moment.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Equipment leads"
        description="Customers with aging equipment — sort by life used and send a replacement quote in one click."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setEquipmentImportOpen(true)}>
              <Upload className="h-4 w-4" /> Import from spreadsheet
            </Button>
            <Button onClick={() => setAddEquipmentOpen(true)}>
              <Plus className="h-4 w-4" /> Add equipment
            </Button>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Customers flagged</p>
          <p className="mt-1 text-2xl font-semibold">{isLoading ? '—' : items.length}</p>
        </Card>
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Equipment items</p>
          <p className="mt-1 text-2xl font-semibold">
            {isLoading ? '—' : items.reduce((n, i) => n + i.equipment.length, 0)}
          </p>
        </Card>
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Potential job value</p>
          <p className="mt-1 text-2xl font-semibold text-brand">{isLoading ? '—' : formatCurrency(totalValue)}</p>
        </Card>
      </div>

      <Card title="Replacement candidates" description="Sorted by highest % of expected equipment life used." noPadding>
        {isLoading ? (
          <div className="divide-y divide-border px-5 py-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="mb-3 h-16 w-full" />
            ))}
          </div>
        ) : items.length ? (
          <div className="divide-y divide-border">
            {items.map((item) => {
              const maxLife = Math.max(...item.equipment.map((e) => e.lifePercent));
              return (
                <div key={item.contactId} className="flex flex-wrap items-start gap-3 px-5 py-4">
                  <Avatar name={item.contactName} className="h-9 w-9" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-text-primary">{item.contactName}</p>
                      {item.quoteSent && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[11px] font-medium text-[#166534]">
                          <CheckCircle2 className="h-3 w-3" /> Quote sent
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[13px] text-text-secondary">
                      {item.agreementTitle ?? 'No active agreement'} · ~{formatCurrency(item.totalEstimatedValue)} potential
                    </p>
                    <div className="mt-2 space-y-1">
                      {item.equipment.map((eq) => (
                        <div key={eq.id} className="flex flex-wrap items-center gap-2 text-[12px] text-text-secondary">
                          <Wrench className="h-3 w-3 text-text-muted" />
                          <span>{eq.name}</span>
                          <span className="text-text-muted">· installed {eq.installYear}</span>
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 font-medium',
                              eq.lifePercent >= 100 ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-brand-subtle text-brand',
                            )}
                          >
                            {eq.lifePercent}% of life used
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="rounded-full bg-bg-subtle px-2.5 py-1 text-xs font-semibold text-brand">
                      {maxLife}% max
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={quote.isPending && quoteOpen?.contactId === item.contactId}
                        onClick={() => {
                          setQuoteOpen(item);
                          setQuoteNotes('');
                        }}
                      >
                        <Send className="h-3.5 w-3.5" /> Send replacement quote
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setConvertOpen(item);
                          setConvertAmount(item.totalEstimatedValue);
                        }}
                      >
                        <Sparkles className="h-3.5 w-3.5" /> Mark converted
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={ArrowUpRight}
            title="No equipment leads yet"
            description="Add equipment install years on customer records or import a spreadsheet — we'll flag aging systems automatically."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => setAddEquipmentOpen(true)}>
                  <Plus className="h-4 w-4" /> Add equipment
                </Button>
                <Button variant="secondary" onClick={() => setEquipmentImportOpen(true)}>
                  <Upload className="h-4 w-4" /> Import from spreadsheet
                </Button>
              </div>
            }
          />
        )}
      </Card>

      <Modal
        open={!!quoteOpen}
        onClose={() => setQuoteOpen(null)}
        title="Send replacement quote"
        description="Enrolls the customer in the upgrade email sequence and logs the outreach."
      >
        {quoteOpen && (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">
              Sending to <strong>{quoteOpen.contactName}</strong> for{' '}
              {quoteOpen.equipment.map((e) => e.name).join(', ')}.
            </p>
            <Input
              label="Notes (optional)"
              value={quoteNotes}
              onChange={(e) => setQuoteNotes(e.target.value)}
              placeholder="e.g. Mention 0% financing, include both furnace and AC"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setQuoteOpen(null)}>
                Cancel
              </Button>
              <Button
                loading={quote.isPending}
                onClick={() =>
                  quote.mutate({
                    contactId: quoteOpen.contactId,
                    notes: quoteNotes.trim() || undefined,
                  })
                }
              >
                Send quote campaign
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!convertOpen}
        onClose={() => setConvertOpen(null)}
        title="Mark upgrade converted"
        description="Records growth revenue and links it to your conversion funnel."
      >
        {convertOpen && (
          <div className="space-y-4">
            <Input
              label="Job revenue"
              type="number"
              prefix="$"
              value={convertAmount || ''}
              onChange={(e) => setConvertAmount(Number(e.target.value) || 0)}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setConvertOpen(null)}>
                Cancel
              </Button>
              <Button
                loading={convert.isPending}
                onClick={() => convert.mutate({ contactId: convertOpen.contactId, amount: convertAmount })}
              >
                Record conversion
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={addEquipmentOpen} onClose={() => setAddEquipmentOpen(false)} title="Add equipment">
        <div className="space-y-4">
          <Select
            label="Customer"
            value={equipmentContactId}
            onChange={(e) => setEquipmentContactId(e.target.value)}
          >
            <option value="">Select a customer…</option>
            {(contactsData?.contacts ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Input
            label="Name"
            value={equipmentForm.name}
            onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
            placeholder="Carrier 80% furnace"
            required
          />
          <Select
            label="Type"
            value={equipmentForm.type}
            onChange={(e) =>
              setEquipmentForm({ ...equipmentForm, type: e.target.value as (typeof EQUIPMENT_TYPES)[number] })
            }
          >
            {EQUIPMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace('_', ' ')}
              </option>
            ))}
          </Select>
          <Input
            label="Install year"
            type="number"
            value={equipmentForm.install_year}
            onChange={(e) =>
              setEquipmentForm({ ...equipmentForm, install_year: Number(e.target.value) || 2000 })
            }
          />
          <Input
            label="Notes (optional)"
            value={equipmentForm.notes}
            onChange={(e) => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setAddEquipmentOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={addEquipment.isPending}
              disabled={!equipmentContactId || !equipmentForm.name.trim()}
              onClick={() => addEquipment.mutate()}
            >
              Save equipment
            </Button>
          </div>
        </div>
      </Modal>

      <CsvImportModal
        open={equipmentImportOpen}
        onClose={() => setEquipmentImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['upgrades'] })}
        defaultKind="equipment"
        allowedKinds={['equipment']}
      />
    </div>
  );
}
